import { Dialog } from "@headlessui/react";
import { v4 as uuidv4 } from "uuid";
import { usePolicyMapper } from "hooks/usePolicyMapper";
import { useForm } from "react-hook-form";
import { sendReminder } from "service/notificationservice/sendReminder";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";

import FormInputError from "components/General/FormInputError";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import LoadingOverlay from "react-loading-overlay";
import { stringFormattingToCurrency } from "js/formatting";
import { Trans, useTranslation } from "react-i18next";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

const AgentSendReminderModal = ({
  isOpenSendReminder,
  setIsOpenSendReminder,
  policyId,
}) => {
  const noteId = uuidv4();
  const { policyDetail } = usePolicyMapper(policyId);
  const { user } = useSelector((s) => s.user);
  const { agentPolicies } = useContext(AgentData);
  const [message, setMessage] = useState(
    "Send reminder successfully sent to customer."
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors: sendReminderErrors },
  } = useForm({
    defaultValues: {},
  });
  const { t } = useTranslation();
  useEffect(() => {
    reset();
  }, [isOpenSendReminder]);

  async function onSubmit(data) {
    let selectedPolicy = agentPolicies.find((x) => x.contractID === policyId);
    if (selectedPolicy) {
      setLoading(true);
      let amount = `${selectedPolicy?.modalPremiumAmountTotal}`;
      let date = selectedPolicy?.premiumDueDate;
      if (amount) {
        await sendReminder(
          data?.email,
          user?.firstName,
          selectedPolicy?.oldPolicy,
          stringFormattingToCurrency(amount),
          date,
          data?.note,
          11
        ).catch((error) => {
          setMessage(t("AgentSendReminderModal-customer-message"));
          setOpenDialog(true);
          setIsOpenSendReminder(false);
          console.log(error);
        });
      }
      setLoading(false);
      setOpenDialog(true);
      setIsOpenSendReminder(false);
    }
  }

  return (
    <>
      <Dialog
        open={isOpenSendReminder}
        onClose={() => setIsOpenSendReminder(false)}
        className="ui-dialogs"
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        {/* Full-screen container to center the panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("AgentPolicyLookup-SendReminderModel")}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label="close the send to customer window"
              onClick={() => setIsOpenSendReminder(false)}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <p className="text-xs">
              {t("AgentPolicyLookup-SendReminderEmail")}
            </p>
            <hr />
            <Dialog.Description>
              <span className="sr-only">
                {t("AgentSendReminderModal-dialog-description")}
              </span>
            </Dialog.Description>
            <LoadingOverlay
              active={loading}
              spinner={
                <EverlakeLoadingSpinner
                  spinnerSize="xs"
                  loadingTextMargin="2"
                  loadingText={t("AgentPolicyLookup-SendReminder")}
                />
              }
              styles={EverlakeLoadingOverlayStyle}
            >
              <div className="mx-auto md:w-4/5">
                <form
                  className="grid grid-cols-1 gap-4"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div>
                    <label forHTML={`${noteId}-email`} className="mb-1 block">
                      {t("AgentSendReminderModal-label-1")}
                    </label>
                    <input
                      id={`${noteId}-email`}
                      className="w-full"
                      type="text"
                      aria-describedby={`${noteId}-emailError`}
                      aria-labelledby="ID_emailLabel"
                      defaultValue={policyDetail?.people?.email}
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: "Please enter a valid email",
                        },
                      })}
                    />
                    {sendReminderErrors.email && (
                      <FormInputError
                        id={`${noteId}-emailError`}
                        errorMessage={sendReminderErrors.email.message}
                      />
                    )}
                  </div>
                  <div>
                    <label forHTML={`${noteId}-note`} className="mb-1 block">
                      {t("AgentSendReminderModal-label-2")}
                    </label>
                    <textarea
                      id={`${noteId}-note`}
                      type="text"
                      rows="4"
                      placeholder="Type message here..."
                      {...register(`note`)}
                    />
                    {sendReminderErrors.note && (
                      <FormInputError
                        id={`${noteId}-note`}
                        errorMessage={sendReminderErrors.note.message}
                      />
                    )}
                  </div>
                  <div className="block text-right">
                    <button
                      className="ui-btn-secondary ui-btn-min-w mt-4"
                      onClick={() => setIsOpenSendReminder(false)}
                    >
                      {t("AgentPolicyLookup-Cancel")}
                      <span className="sr-only">
                        {" "}
                        {t("AgentSendReminderModal-button-1")}
                      </span>
                    </button>
                    <button className="ui-btn-primary ui-btn-min-w mt-4">
                      {t("AgentPolicyLookup-Send")}
                      <span className="sr-only">
                        {t("AgentSendReminderModal-button-2")}
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </LoadingOverlay>
          </Dialog.Panel>
        </div>
      </Dialog>
      <GeneralPurposeDialog
        state={openDialog}
        setState={setOpenDialog}
        title={t("AgentSendReminderModal-generalpurposedialog-title")}
        desc={message}
      />
    </>
  );
};

export default AgentSendReminderModal;
